/**
 * Campaign-invite API client (raise-owner portal, Stage 2C — the invite → account
 * → claim funnel). Talks to the Stage-2A/2B owner-claim endpoints:
 *   • GET  /api/owner/claims/invite/:token   → PUBLIC. Validate a recipient-bound
 *          invite token and return the project teaser + masked recipient email +
 *          verification tier. No auth required (the funnel starts logged-out).
 *   • POST /api/owner/claims/accept-invite    → AUTHENTICATED. Consume the invite
 *          for the signed-in user; the server stamps the `raise_owner` custom
 *          claim and returns the post-claim status + redirect.
 *
 * Mirrors `ownerApi.ts`:
 *   • the outer `{ success, data }` envelope is unwrapped to the inner `data`,
 *   • we reuse `createAuthHeaders` (Bearer attached only when signed in — harmless
 *     on the public validate call) and `fetchWithActivity` (activity tracking +
 *     session refresh),
 *   • documented status codes are translated into a single discriminable error
 *     class the funnel UI can branch on by `code`.
 *
 * TYPED ERROR MAPPING — both endpoints distinguish failure modes by a body
 * `{ code }` (two of them, `expired`/`revoked`, even share HTTP 410), so a plain
 * status switch is insufficient. {@link OwnerInviteError} carries a NORMALIZED
 * `code` so the landing renders the right copy/affordance without string-parsing:
 *   • validate: 400 invalid · 410 expired · 409 consumed · 410 revoked
 *   • accept:   403 email_mismatch · 409 consumed · 410 expired|revoked|invalid ·
 *               401 unauthenticated
 */

import {
  assertAuthIdentity,
  captureAuthIdentity,
  createAuthHeaders,
  fetchWithActivity,
  getApiBaseUrl,
} from '@/utils/apiUtils';

// ── DTOs (mirror the Stage-2B backend contract) ──────────────────────────────

/** The verification tier the invite was issued at. */
export type InviteTier = 'auto_verify' | 'needs_review';

/** The project teaser surfaced on the invite landing (no PII). */
export interface InviteProject {
  projectId: number;
  name: string;
  slug: string | null;
  sector: string;
  stage: string;
  region: string;
}

/** The 200 payload of `GET /claims/invite/:token`. */
export interface InviteValidation {
  project: InviteProject;
  /** The recipient mailbox, masked by the server (e.g. `j***@acme.com`). */
  recipientEmailMasked: string;
  tier: InviteTier;
}

/** The post-claim status returned by `POST /claims/accept-invite`. */
export type AcceptInviteStatus = 'verified' | 'pending_review';

/** The 200 payload of `POST /claims/accept-invite`. */
export interface AcceptInviteResult {
  status: AcceptInviteStatus;
  projectSlug: string | null;
  /** Where the client should route a `verified` claimant (the owner console). */
  redirectTo: string;
  /**
   * Whether the `raise_owner` role is confirmed stamped server-side at the moment
   * this response was produced (or the caller already holds an elevated dual-role
   * that grants owner access and does not need it). `false` means a brand-new
   * account's inline stamp did NOT succeed — the client must reconcile the role
   * (see {@link ReconcileRoleResult}) before entering the console.
   *
   * NOTE: retained for diagnostics/forward-compat, but it is NOT an authorizer for
   * navigation — entering the console is gated SOLELY on `reconcile-role` + a claims
   * refresh confirming `isRaiseOwner`. Routing on this hint alone could carry a
   * stale/unrefreshed token into the console and 403.
   */
  roleReady: boolean;
}

/**
 * The 200 payload of `POST /claims/reconcile-role` — the idempotent role-finalizer
 * the funnel calls after a `verified` accept to GUARANTEE the `raise_owner` role is
 * stamped (and the backend auth cache invalidated) before routing into the console.
 */
export interface ReconcileRoleResult {
  /**
   * The resulting access state: `true` when the caller is now `raise_owner` OR
   * already holds an elevated dual-role that grants owner access. This is the
   * authoritative server-side answer the client gates navigation on.
   */
  isRaiseOwner: boolean;
  /** Whether the uid holds a VERIFIED `ar_project_claims` row. */
  hasVerifiedClaim: boolean;
  /** `true` iff THIS call just stamped the role (vs. it already being present). */
  reconciled: boolean;
}

// ── Typed error ───────────────────────────────────────────────────────────────

/** The normalized failure mode of an invite validate/accept call. */
export type InviteErrorCode =
  | 'invalid'
  | 'expired'
  | 'consumed'
  | 'revoked'
  | 'email_mismatch'
  | 'unauthenticated'
  | 'unknown';

/**
 * A failure of the invite funnel. `code` is normalized from the response status
 * and body so the landing can branch deterministically; `status` is preserved
 * for diagnostics. Messages are domain-safe (no PII).
 */
export class OwnerInviteError extends Error {
  readonly status: number;
  readonly code: InviteErrorCode;
  constructor(message: string, status: number, code: InviteErrorCode) {
    super(message);
    this.name = 'OwnerInviteError';
    this.status = status;
    this.code = code;
    // Preserve the prototype chain across the TS/ES5 `extends Error` transpile so
    // `instanceof OwnerInviteError` keeps working in the UI.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const KNOWN_CODES: ReadonlySet<string> = new Set<InviteErrorCode>([
  'invalid',
  'expired',
  'consumed',
  'revoked',
  'email_mismatch',
  'unauthenticated',
]);

const DEFAULT_MESSAGES: Record<InviteErrorCode, string> = {
  invalid: 'This invitation link is no longer valid.',
  expired: 'This invitation link has expired.',
  consumed: 'This invitation has already been used.',
  revoked: 'This invitation has been revoked.',
  email_mismatch: 'Please use the email address the invitation was sent to.',
  unauthenticated: 'Please sign in to claim this project.',
  unknown: 'We could not process this invitation. Please try again.',
};

// ── Response handling ─────────────────────────────────────────────────────────

/** The outer transport envelope every owner route returns on success. */
interface ApiEnvelope<T> {
  success: true;
  data: T;
}

/** Best-effort extraction of `{ code, message }` from a non-2xx body. */
async function readErrorBody(response: Response): Promise<{ code?: string; message?: string }> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const text = await response.text().catch(() => '');
    return { message: text || undefined };
  }
  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!body) return {};
  const code = typeof body.code === 'string' ? body.code : undefined;
  const message =
    typeof body.error === 'string'
      ? body.error
      : typeof body.message === 'string'
        ? body.message
        : undefined;
  return { code, message };
}

/**
 * Normalize (status, body code) into an {@link InviteErrorCode}. Status wins for
 * the auth-shaped failures (403 is always the recipient mismatch; 401 is always
 * unauthenticated); otherwise a recognized body `code` is preferred so
 * `expired`/`revoked` (both HTTP 410) stay distinct, falling back to a sensible
 * status-derived default.
 */
function normalizeCode(status: number, bodyCode: string | undefined): InviteErrorCode {
  if (status === 403) return 'email_mismatch';
  if (status === 401) return 'unauthenticated';
  if (bodyCode && KNOWN_CODES.has(bodyCode)) return bodyCode as InviteErrorCode;
  switch (status) {
    case 409:
      return 'consumed';
    case 400:
    case 410:
      return 'invalid';
    default:
      return 'unknown';
  }
}

async function toInviteError(response: Response): Promise<OwnerInviteError> {
  const { code, message } = await readErrorBody(response);
  const normalized = normalizeCode(response.status, code);
  return new OwnerInviteError(message ?? DEFAULT_MESSAGES[normalized], response.status, normalized);
}

export const ownerInviteApi = {
  /**
   * GET /api/owner/claims/invite/:token — validate a recipient-bound invite.
   * PUBLIC: no auth is required (the funnel starts logged-out), and any Bearer the
   * helper attaches when a session happens to exist is ignored by the server.
   */
  validateInvite: async (token: string): Promise<InviteValidation> => {
    // §4.3 identity safety (CX#7): one snapshot at entry, threaded through headers + delivery +
    // every post-body-read fence. This funnel is directly downstream of InviteClaim's account
    // switch, so an A→B switch mid-flight must never send A's bearer under B nor deliver A's body.
    const requestIdentity = captureAuthIdentity();
    const headers = await createAuthHeaders({}, 'GET', requestIdentity);
    const response = await fetchWithActivity(
      `${getApiBaseUrl()}/api/owner/claims/invite/${encodeURIComponent(token)}`,
      { method: 'GET', headers },
      requestIdentity,
    );
    if (!response.ok) {
      const error = await toInviteError(response);
      assertAuthIdentity(requestIdentity);
      throw error;
    }
    const json = (await response.json()) as ApiEnvelope<InviteValidation>;
    assertAuthIdentity(requestIdentity);
    return json.data;
  },

  /**
   * POST /api/owner/claims/accept-invite — consume the invite for the signed-in
   * user. AUTHENTICATED: the caller's Firebase ID token must belong to the invited
   * recipient (the server fails closed with 403 otherwise). On success the server
   * stamps the `raise_owner` custom claim, so the caller MUST force an ID-token
   * refresh afterwards to pick up the new role.
   */
  acceptInvite: async (token: string): Promise<AcceptInviteResult> => {
    const requestIdentity = captureAuthIdentity();
    const headers = await createAuthHeaders({}, 'POST', requestIdentity);
    const response = await fetchWithActivity(`${getApiBaseUrl()}/api/owner/claims/accept-invite`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token }),
    }, requestIdentity);
    if (!response.ok) {
      const error = await toInviteError(response);
      assertAuthIdentity(requestIdentity);
      throw error;
    }
    const json = (await response.json()) as ApiEnvelope<AcceptInviteResult>;
    assertAuthIdentity(requestIdentity);
    return json.data;
  },

  /**
   * POST /api/owner/claims/reconcile-role — idempotently finalize the signed-in
   * user's `raise_owner` role. AUTHENTICATED, no request body. Mounted ABOVE the
   * global approval gate (alongside accept-invite), so a freshly-stamped owner — who
   * is never `approved` — can reach it. The server stamps the role when the uid holds
   * a VERIFIED claim and is eligible (no-downgrade), invalidates its auth cache, and
   * ALWAYS returns 200 with the resulting access state. Safe to call repeatedly: the
   * funnel uses it to GUARANTEE + confirm the role before entering the console, and
   * the finalizing-state retry button re-runs it.
   */
  reconcileRole: async (): Promise<ReconcileRoleResult> => {
    const requestIdentity = captureAuthIdentity();
    const headers = await createAuthHeaders({}, 'POST', requestIdentity);
    const response = await fetchWithActivity(`${getApiBaseUrl()}/api/owner/claims/reconcile-role`, {
      method: 'POST',
      headers,
    }, requestIdentity);
    if (!response.ok) {
      const error = await toInviteError(response);
      assertAuthIdentity(requestIdentity);
      throw error;
    }
    const json = (await response.json()) as ApiEnvelope<ReconcileRoleResult>;
    assertAuthIdentity(requestIdentity);
    return json.data;
  },
};

export default ownerInviteApi;
